<template>
	<div class="monitoring-stock">
		<div class="container">
			<div class="page-header flex-center">
				<h1 class="page-title">Monitoring Stock</h1>

				<div class="page-header-right text-right ml-a">
					<daterangepicker
						:presets="filterDatePresets"
						:start="stockData.params.since"
						:end="stockData.params.until"
						@selected="setFilterDate"
						ref="datePicker"
					>
					</daterangepicker>
				</div>
			</div>

			<div class="page-content">
				<div class="table-list-menu">
					<div class="row">
						<div class="col-sm-3">
							<multiselect
								v-model="supplier.selected"
								:show-labels="false"
								track-by="_id"
								label="label"
								placeholder="id/email/nama supplier"
								:options="supplier.options"
								:loading="supplier.loading"
								@search-change="fetchDataSupplier"
							>
								<span slot="noResult">No options matching the query</span>
								<span slot="noOptions">No options available</span>
							</multiselect>
						</div>

						<div class="col-sm-9">
							<div class="d-flex align-items-center justify-content-end ml-a">
								<button
									@click="exportData"
									type="button"
									class="export-button btn btn-sm btn-primary"
									:class="{ 'is-loading': exportStock.loading }"
									:disabled="exportStock.disabled"
									v-tooltip="{
										content: exportStock.disabled
											? 'Tidak dapat meng-export data kosong'
											: '',
									}"
								>
									<i class="material-icons">save_alt</i>
									<span class="mr-2">{{ $t('Export') }}</span>
								</button>
							</div>
						</div>
					</div>
				</div>

				<div class="table-responsive">
					<table class="table table-primary table-striped">
						<thead>
							<tr>
								<th
									v-for="(column, index) in stockData.columns"
									:key="index"
									:class="`${column.class} ${sortClass(column.key)}`"
									:width="column.width"
									@click="changeSorting(column.key)"
								>
									<div class="d-flex">
										<span>
											{{ column.label }}
										</span>
										<span
											v-if="column.tooltip"
											class="ml-1"
											v-tooltip="column.tooltip"><i
											class="material-icons">info</i>
										</span>
									</div>
								</th>
							</tr>
						</thead>

						<tbody>
							<tr v-for="(item, index) in stockData.data" :key="index">
								<td>
									<div class="row">
										<div class="col-md-9">
											{{ index + 1 }}
										</div>
									</div>
								</td>

								<td>
									<div class="row">
										<div class="col-md-9">
											<a
												:href="
													'/admin/products?product_id=' + item.product_id
												"
												target="_blank"
											>
												{{ item.name }}
											</a>
										</div>
									</div>
								</td>

								<td>
									<div class="row">
										<div class="col-md-9">
											<a
												href="#"
												@click="(event) => handleShowSupplier(event, item)"
											>
												{{ item.supplier.name }}
											</a>
										</div>
									</div>
								</td>

								<td>
									<div class="row">
										<div class="col-md-9">
											{{ item.stock }}
										</div>
									</div>
								</td>

								<td>
									<div class="row">
										<div class="col-md-9">
											{{ item.order_count }}
										</div>
									</div>
								</td>
							</tr>
						</tbody>
					</table>

					<div class="table-fetch-status" v-if="stockData.loading">
						<div class="is-loading large dark m-auto"></div>
					</div>

					<div class="list-empty" v-if="stockData.data.length <= 0 && !stockData.loading">
						<i class="material-icons mr-2">error_outline</i>
						<span>No Stock was found</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal Supplier Profile -->
		<modal-supplier-profile
			v-if="stockData.showSupplier"
			title="Detail Supplier"
			:user="stockData.selectedItem.supplier"
			:isShowIdentity="false"
			:isShowBank="false"
			:isShowLocation="false"
			:isShowPicture="false"
			:isShowStats="false"
			@close="closeSupplierProfile"
		/>

		<!-- Notifications -->
		<notifications group="monitoringStock" position="bottom right"></notifications>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import Daterangepicker from '@/components/Daterangepicker.vue';
import adminApi from '@/api/admin';
import monitoringStockApi from '@/api/monitoring-stock';
import { downloadFile } from '@/lib/helper';
import ModalSupplierProfile from '@/components/ModalSupplierProfile.vue';

export default {
	name: 'monitoring-stock',
	components: {
		Daterangepicker,
		ModalSupplierProfile,
	},
	data() {
		return {
			supplier: {
				selected: null,
				options: [],
				loading: false,
			},
			stockData: {
				columns: [
					{ key: '', label: 'No', class: 'text-left', width: '4%' },
					{
						key: 'name',
						label: 'Product Name',
						class: 'th-sort text-left',
						width: '20%',
					},
					{
						key: 'supplier',
						label: 'Supplier Name',
						class: 'th-sort text-left',
						width: '20%',
					},
					{
						key: 'stock',
						label: 'Stock',
						class: 'th-sort text-left',
						width: '10%',
						tooltip: 'Data Stok Saat Ini',
					},
					{
						key: 'order_count',
						label: 'Total Order',
						class: 'th-sort text-left',
						width: '10%',
					},
				],
				params: {
					page: 1,
					limit: 25,
					sort: 'desc',
					sort_by: 'order_count',
					since: '',
					until: '',
					supplier_id: '',
				},
				data: [],
				loading: false,
				isHaveMore: false,
				selectedItem: {},
				showSupplier: false,
			},
			exportStock: {
				loading: false,
				disabled: false,
			},
		};
	},
	computed: {
		...mapGetters({
			user: 'user',
			isAdmin: 'isAdmin',
			filterDatePresets: 'filterDatePresets',
		}),
	},
	methods: {
		initData() {
			this.initFilterDate();
			this.fetchMonitoringStock(true);
		},
		resetTable() {
			this.stockData.params.page = 1;
			this.stockData.data = [];
		},
		initFilterDate() {
			const dateFormat = 'YYYY-MM-DD';
			const start = dayjs().startOf('day');
			const end = dayjs().endOf('day');

			this.filterDate = { start, end };
			this.stockData.params.since = start.format(dateFormat);
			this.stockData.params.until = end.format(dateFormat);
		},
		setFilterDate(filterDate) {
			const dateFormat = 'YYYY-MM-DD';

			this.filterDate = filterDate;
			this.stockData.params.since = dayjs(filterDate.start).format(dateFormat);
			this.stockData.params.until = dayjs(filterDate.end).format(dateFormat);

			this.$router.push({
				query: {
					...this.$route.query,
					since: this.stockData.params.since,
					until: this.stockData.params.until,
				},
			});

			this.fetchMonitoringStock(true);
		},
		fetchDataSupplier(query) {
			this.supplier.loading = true;

			const params = {
				keyword: query,
				role: 'owner',
				owner_type: 'supplier',
			};

			this.ooDelay(() => {
				if (query !== '') {
					adminApi.searchUser(
						params,
						(response) => {
							this.supplier.options = response.data;
							this.supplier.loading = false;
						},
						() => {
							this.supplier.loading = false;
						},
					);
				}
			}, 1000);
		},
		sortClass(key) {
			// eslint-disable-next-line no-nested-ternary
			return key
				? this.stockData.params.sort_by === key
					? `th-sort-${this.stockData.params.sort}`
					: ''
				: '';
		},
		changeSorting(key) {
			if (key === '') return;
			const sort =
				this.stockData.params.sort_by === key && this.stockData.params.sort === 'asc'
					? 'desc'
					: 'asc';

			this.stockData.params.sort_by = key;
			this.stockData.params.sort = sort;

			this.fetchMonitoringStock(true);
		},
		fetchMonitoringStock(resetList = false) {
			if (resetList) this.resetTable();

			this.stockData.loading = true;
			this.exportStock.disabled = true;

			const callback = (response) => {
				const items = response.data;

				this.stockData.data = resetList ? items : this.stockData.data.concat(items);
				this.stockData.isHaveMore = items.length > 0;

				if (this.stockData.isHaveMore) this.stockData.params.page++;

				this.stockData.loading = false;
				this.supplier.loading = false;

				this.exportStock.disabled = this.stockData.data.length <= 0;
			};

			const errorCallback = () => {
				this.stockData.isHaveMore = false;
				if (resetList) this.stockData.data = [];

				this.stockData.loading = false;
				this.supplier.loading = false;

				this.exportStock.disabled = this.stockData.data.length <= 0;
			};

			monitoringStockApi.index(this.stockData.params, callback, errorCallback);
		},
		handleScroll() {
			if (
				// eslint-disable-next-line operator-linebreak
				!this.stockData.loading &&
				// eslint-disable-next-line operator-linebreak
				this.stockData.isHaveMore &&
				window.innerHeight + window.scrollY >= document.documentElement.offsetHeight - 50
			) {
				this.fetchMonitoringStock();
			}
		},
		exportData() {
			this.exportStock.loading = true;

			const callback = (response) => {
				downloadFile(response.data);

				this.$notify({
					group: 'monitoringStock',
					text: 'Export data berhasil',
					type: 'success',
				});

				this.exportStock.loading = false;
			};

			const errorCallback = () => {
				this.$notify({
					group: 'monitoringStock',
					text: 'Gagal meng-export data',
					type: 'error',
				});

				this.exportStock.loading = false;
			};

			monitoringStockApi.export(this.stockData.params, callback, errorCallback);
		},
		handleShowSupplier(event, item) {
			event.preventDefault();
			this.stockData.selectedItem = item;
			this.stockData.showSupplier = true;
		},
		closeSupplierProfile() {
			this.stockData.selectedItem = {};
			this.stockData.showSupplier = false;
		},
	},
	watch: {
		'supplier.selected': {
			handler(newValue) {
				this.stockData.params.supplier_id = newValue._id;
				this.fetchMonitoringStock(true);
			},
		},
	},
	created() {
		this.initData();
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style scoped lang="scss">
.export-button {
	height: 33px;
}
</style>
